<template>
  <div class="book-match-page">
    <section class="hero">
      <div>
        <p class="eyebrow">EXCEL 图书数据补全 <span class="feature-badge">全新上线</span></p>
        <h1>批量匹配图书信息</h1>
        <p>下载标准模板并填写 ISBN，上传后自动匹配图书信息。重复、未匹配和非法 ISBN 均不收费。</p>
      </div>
      <router-link class="history-link" to="/myBookMatch">我的匹配任务</router-link>
    </section>

    <el-card class="workflow-card" shadow="never">
      <el-steps :active="activeStep" finish-status="success" :direction="isMobile ? 'vertical' : 'horizontal'">
        <el-step title="上传模板" description="固定单列 ISBN" />
        <el-step title="匹配数据" description="后台安全处理" />
        <el-step title="报价支付" description="只为成功项付费" />
        <el-step title="下载结果" description="统一导出 XLSX" />
      </el-steps>

      <div v-if="!task.id" class="upload-panel">
        <div class="template-guide">
          <div>
            <h2>先下载标准模板</h2>
            <p>第一行固定为 <strong>isbn</strong>，从第二行开始在 A 列填写；不要新增其他列。</p>
          </div>
          <el-button icon="el-icon-download" :loading="templateDownloading" @click="downloadTemplate">
            下载 ISBN 模板
          </el-button>
        </div>
        <el-upload
          drag
          action="#"
          accept=".xlsx"
          :show-file-list="false"
          :before-upload="beforeUpload"
          :http-request="uploadFile"
        >
          <i class="el-icon-upload" />
          <div class="el-upload__text">把填写好的模板拖到这里，或<em>点击选择</em></div>
          <div slot="tip" class="el-upload__tip">仅支持标准 .xlsx 模板，最大 20MB、5 万条 ISBN</div>
        </el-upload>
        <el-progress v-if="uploading" class="upload-progress" :percentage="uploadProgress" />
        <p v-if="uploading && uploadProgress >= 100" class="validating-tip">上传完成，正在校验模板并创建任务…</p>
        <div class="privacy-note"><i class="el-icon-lock" /> 文件私有存储，仅本人登录后可下载结果</div>
      </div>

      <div v-else-if="isProcessing || task.status === 'FAILED'" class="processing-panel">
        <template v-if="task.status !== 'FAILED'">
          <div class="processing-icon"><i class="el-icon-loading" /></div>
          <h2>{{ statusText(task.status) }}</h2>
          <p>任务在后台运行，可以离开页面，稍后从“我的匹配任务”继续查看。</p>
          <el-progress :percentage="task.progress || 0" :stroke-width="12" />
          <div class="processing-meta">任务 #{{ task.id }} · 页面刷新后会自动恢复</div>
          <el-button v-if="task.canCancel" class="processing-cancel" type="text" @click="cancelTask">取消排队任务</el-button>
        </template>
        <template v-else>
          <div class="failed-icon"><i class="el-icon-warning-outline" /></div>
          <h2>本次处理没有完成</h2>
          <p>{{ task.errorMessage || '处理失败，请重试' }}</p>
          <div class="main-action">
            <el-button v-if="task.canRetry" type="primary" :loading="retrying" @click="retryTask">重新处理</el-button>
            <el-button v-if="task.canCancel" @click="cancelTask">取消任务</el-button>
          </div>
        </template>
      </div>

      <div v-else-if="task.status === 'READY_TO_PAY'" class="quote-panel">
        <div class="panel-title">
          <div>
            <h2>匹配完成，确认报价</h2>
            <p>最终结果文件已生成并锁定，支付后立即开放下载</p>
          </div>
          <el-tag type="warning">报价 24 小时有效</el-tag>
        </div>
        <stat-grid :task="task" />
        <div class="charge-rule">
          <div><strong>{{ money(task.payableAmountFen) }}</strong><span>应付金额</span></div>
          <p>{{ money(task.unitPriceFen) }}/成功匹配的唯一 ISBN<br>重复行全部填充，但不会重复计费</p>
        </div>
        <sample-list :samples="task.samples || []" />
        <div class="quote-actions">
          <el-button v-if="task.canCancel" type="text" @click="cancelTask">取消任务</el-button>
          <el-button :loading="issuesDownloading" @click="downloadIssues">下载免费问题报告</el-button>
          <el-button type="primary" :loading="paySubmitting" :disabled="!task.canPay" @click="openPayment">
            微信扫码支付 {{ money(task.payableAmountFen) }}
          </el-button>
        </div>
        <p v-if="!task.canPay" class="expired-tip">报价已过期，请重新上传文件。</p>
      </div>

      <div v-else-if="canDownloadResult" class="download-panel">
        <div class="success-mark"><i class="el-icon-circle-check" /></div>
        <h2>{{ task.status === 'FREE_READY' ? '无需支付，结果已准备好' : '支付成功，结果已准备好' }}</h2>
        <p>结果文件保留至 {{ formatTime(task.resultExpireTime) }}，请及时下载保存。</p>
        <stat-grid :task="task" />
        <div class="quote-actions">
          <el-button :loading="issuesDownloading" @click="downloadIssues">下载问题报告</el-button>
          <el-button type="success" :loading="resultDownloading" @click="downloadResult">下载完整匹配结果</el-button>
        </div>
      </div>

      <div v-else-if="isTerminal" class="terminal-panel">
        <div class="failed-icon"><i class="el-icon-time" /></div>
        <h2>{{ terminalTitle }}</h2>
        <p>{{ terminalMessage }}</p>
        <el-button type="primary" @click="startNew">重新上传文件</el-button>
      </div>
    </el-card>

    <div v-if="showStickyAction" class="mobile-sticky">
      <el-button v-if="task.status === 'READY_TO_PAY'" type="primary" :loading="paySubmitting" :disabled="!task.canPay" @click="openPayment">支付 {{ money(task.payableAmountFen) }}</el-button>
      <el-button v-else-if="canDownloadResult" type="success" :loading="resultDownloading" @click="downloadResult">下载结果</el-button>
    </div>

    <el-dialog
      :visible.sync="cancelDialogVisible"
      :width="isMobile ? '92%' : '420px'"
      :show-close="false"
      :close-on-click-modal="false"
      :close-on-press-escape="!cancelDialogSubmitting"
      custom-class="cancel-confirm-dialog"
      @close="closeCancelDialog"
    >
      <div class="cancel-dialog-body">
        <div class="cancel-icon-wrap"><i class="el-icon-warning-outline" /></div>
        <h3>确定要取消这个匹配任务？</h3>
        <p>取消后任务会立即停止，已匹配的数据不会被保留。<br>如需再次匹配，需要重新上传模板文件。</p>
        <div class="cancel-hint"><i class="el-icon-info" />未完成支付不会产生任何费用</div>
      </div>
      <div slot="footer" class="cancel-dialog-footer">
        <el-button :disabled="cancelDialogSubmitting" @click="closeCancelDialog">继续处理</el-button>
        <el-button type="danger" :loading="cancelDialogSubmitting" @click="confirmCancelTask">确认取消</el-button>
      </div>
    </el-dialog>

    <el-dialog title="微信扫码支付" :visible.sync="payDialog" :width="isMobile ? '92%' : '420px'" @close="onPayDialogClose">
      <div class="pay-dialog">
        <div class="pay-amount">{{ money(task.payableAmountFen) }}</div>
        <p>共 {{ formatCount(task.matchedUniqueCount) }} 个成功匹配的唯一 ISBN</p>
        <img v-if="qrCodeData" :src="qrCodeData" alt="微信支付二维码">
        <p class="pay-help">请使用微信扫码完成支付。关闭弹窗不会取消订单，可随时回来继续。</p>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import QRCode from 'qrcode'
import {
  cancelBookMatch,
  downloadBookMatch,
  downloadBookMatchTemplate,
  getBookMatchTask,
  retryBookMatch,
  saveDownload,
  uploadBookMatch
} from '@/api/bookMatch'
import { submitBookMatch } from '@/api/pay'
import StatGrid from '@/components/book-match/StatGrid.vue'
import SampleList from '@/components/book-match/SampleList.vue'

const PROCESSING = ['QUEUED', 'PARSING', 'MATCHING', 'GENERATING']

export default {
  name: 'BookMatch',
  components: { StatGrid, SampleList },
  data () {
    return {
      task: {},
      uploading: false,
      uploadProgress: 0,
      templateDownloading: false,
      retrying: false,
      paySubmitting: false,
      issuesDownloading: false,
      resultDownloading: false,
      cancelDialogVisible: false,
      cancelDialogSubmitting: false,
      payDialog: false,
      qrCodeData: '',
      paymentWatching: false,
      pollTimer: null,
      pollCount: 0,
      isMobile: window.innerWidth <= 768
    }
  },
  computed: {
    isProcessing () {
      return PROCESSING.includes(this.task.status)
    },
    isReady () {
      return this.task.status === 'PAID_READY' || this.task.status === 'FREE_READY'
    },
    canDownloadResult () {
      return this.isReady && this.task.canDownload !== false
    },
    isTerminal () {
      return ['DRAFT', 'CANCELLED', 'EXPIRED'].includes(this.task.status) || (this.isReady && !this.canDownloadResult)
    },
    terminalTitle () {
      if (this.task.status === 'DRAFT') return '模板规则已更新'
      return this.task.status === 'CANCELLED' ? '任务已取消' : '结果文件已过期'
    },
    terminalMessage () {
      if (this.task.status === 'DRAFT') return '请下载最新标准模板，填写后重新上传。'
      return this.task.status === 'CANCELLED'
        ? '这个任务不会再继续处理，你可以重新上传文件开始匹配。'
        : '为保护数据安全，文件已按保留期限清理，请重新上传后再匹配。'
    },
    activeStep () {
      if (this.canDownloadResult) return 4
      if (this.isTerminal) return 3
      if (this.task.status === 'READY_TO_PAY') return 2
      if (this.isProcessing || this.task.status === 'FAILED') return 1
      return 0
    },
    showStickyAction () {
      return this.isMobile && (this.task.status === 'READY_TO_PAY' || this.canDownloadResult)
    }
  },
  watch: {
    '$route.params.id' (taskId) {
      if (taskId && String(taskId) !== String(this.task.id || '')) this.restoreTask(taskId)
      if (!taskId && this.task.id) this.resetView()
    }
  },
  created () {
    window.addEventListener('resize', this.handleResize)
    if (this.$route.params.id) this.restoreTask(this.$route.params.id)
  },
  beforeDestroy () {
    this.stopPolling()
    window.removeEventListener('resize', this.handleResize)
  },
  methods: {
    handleResize () {
      this.isMobile = window.innerWidth <= 768
    },
    beforeUpload (file) {
      const extension = (file.name.split('.').pop() || '').toLowerCase()
      if (extension !== 'xlsx') {
        this.$toast.fail('请上传下载的 xlsx 标准模板')
        return false
      }
      if (file.size > 20 * 1024 * 1024) {
        this.$toast.fail('文件不能超过 20MB')
        return false
      }
      return true
    },
    async downloadTemplate () {
      if (this.templateDownloading) return
      this.templateDownloading = true
      try {
        const response = await downloadBookMatchTemplate()
        saveDownload(response, '图书批量匹配模板.xlsx')
        this.$toast.success('模板已下载，请从第二行开始填写 ISBN')
      } finally {
        this.templateDownloading = false
      }
    },
    async uploadFile (options) {
      this.stopPolling()
      this.uploading = true
      this.uploadProgress = 0
      try {
        const response = await uploadBookMatch(options.file, progress => { this.uploadProgress = progress })
        this.task = response.data
        this.$router.replace('/bookMatch/task/' + this.task.id).catch(() => {})
        options.onSuccess(response)
        this.startPolling(true)
      } catch (error) {
        options.onError(error)
      } finally {
        this.uploading = false
      }
    },
    async restoreTask (taskId) {
      try {
        const response = await getBookMatchTask(taskId, false)
        this.task = response.data
        // 仅在用户主动打开支付弹窗时才轮询，刷新或重新进入页面不自动开启
        this.paymentWatching = false
        if (this.shouldPoll()) {
          this.startPolling(false)
        }
      } catch (error) {
        this.$router.replace('/bookMatch')
      }
    },
    startPolling (immediate) {
      this.stopPolling()
      this.pollCount = 0
      this.schedulePoll(immediate ? 200 : 2000)
    },
    schedulePoll (delay) {
      this.pollTimer = window.setTimeout(async () => {
        this.pollTimer = null
        try {
          const response = await getBookMatchTask(this.task.id, true)
          this.task = response.data
          if (this.canDownloadResult) {
            this.paymentWatching = false
            this.payDialog = false
            this.$toast.success(this.task.status === 'PAID_READY' ? '支付成功，结果已解锁' : '匹配完成，可免费下载')
          }
        } catch (error) {
          // 网络恢复后下一次轮询会自动重试
        }
        this.pollCount += 1
        if (this.shouldPoll()) this.schedulePoll(this.pollCount < 15 ? 2000 : 5000)
      }, delay)
    },
    shouldPoll () {
      // 匹配处理中需要更新进度；支付轮询仅在用户主动打开支付弹窗、且状态仍处于待支付时执行。
      if (PROCESSING.includes(this.task.status)) return true
      return this.payDialog && this.paymentWatching &&
        this.task.status === 'READY_TO_PAY' && this.task.canPay !== false
    },
    stopPolling () {
      if (this.pollTimer) window.clearTimeout(this.pollTimer)
      this.pollTimer = null
    },
    async retryTask () {
      this.retrying = true
      try {
        await retryBookMatch(this.task.id)
        const response = await getBookMatchTask(this.task.id, true)
        this.task = response.data
        this.startPolling(true)
      } finally {
        this.retrying = false
      }
    },
    async cancelTask () {
      if (this.cancelDialogSubmitting) return
      this.cancelDialogVisible = true
    },
    closeCancelDialog () {
      if (this.cancelDialogSubmitting) return
      this.cancelDialogVisible = false
    },
    async confirmCancelTask () {
      if (this.cancelDialogSubmitting) return
      this.cancelDialogSubmitting = true
      try {
        await cancelBookMatch(this.task.id)
        this.cancelDialogVisible = false
        this.resetView()
      } catch (error) {
        // 请求层已提示
      } finally {
        this.cancelDialogSubmitting = false
      }
    },
    async startNew () {
      if (this.task.status === 'DRAFT' && this.task.id) {
        try { await cancelBookMatch(this.task.id) } catch (error) { /* 请求层已提示 */ }
      }
      this.resetView()
    },
    resetView () {
      this.stopPolling()
      this.task = {}
      this.uploadProgress = 0
      this.paymentWatching = false
      if (this.$route.path !== '/bookMatch') this.$router.replace('/bookMatch')
    },
    async openPayment () {
      if (this.paySubmitting || !this.task.canPay) return
      this.paySubmitting = true
      try {
        const response = await submitBookMatch(this.task.id)
        this.qrCodeData = await QRCode.toDataURL(response.data.codeUrl, { width: 260, margin: 1 })
        this.payDialog = true
        this.paymentWatching = true
        this.startPolling(false)
      } finally {
        this.paySubmitting = false
      }
    },
    onPayDialogClose () {
      this.qrCodeData = ''
      // 关闭支付弹窗后立即停止轮询，避免后台持续请求任务详情
      this.paymentWatching = false
      this.stopPolling()
    },
    async downloadIssues () {
      this.issuesDownloading = true
      try {
        const response = await downloadBookMatch(this.task.id, true)
        saveDownload(response, '未匹配与无效.xlsx')
        this.$toast.success('问题报告已下载')
      } finally {
        this.issuesDownloading = false
      }
    },
    async downloadResult () {
      this.resultDownloading = true
      try {
        const response = await downloadBookMatch(this.task.id, false)
        saveDownload(response, '图书匹配结果.xlsx')
        this.$toast.success('完整结果已下载')
      } finally {
        this.resultDownloading = false
      }
    },
    statusText (status) {
      return { QUEUED: '任务已排队', PARSING: '正在读取表格', MATCHING: '正在匹配本地图书库', GENERATING: '正在生成结果文件' }[status] || '正在处理'
    },
    money (fen) {
      return '¥' + ((Number(fen) || 0) / 100).toFixed(2)
    },
    formatCount (value) {
      return (Number(value) || 0).toLocaleString('zh-CN')
    },
    formatTime (value) {
      return value ? String(value).replace('T', ' ') : '文件过期前'
    }
  }
}
</script>

<style lang="less" scoped>
.book-match-page { max-width: 1200px; margin: 0 auto; padding: 32px 20px 80px; color: #1f2937; }
.hero { display: flex; justify-content: space-between; align-items: flex-end; padding: 26px 30px; margin-bottom: 22px; border-radius: 18px; color: #fff; background: linear-gradient(135deg, #1769e0, #16a085); box-shadow: 0 16px 38px rgba(23, 105, 224, .18); }
.hero h1 { margin: 4px 0 10px; font-size: 32px; }
.hero p { margin: 0; line-height: 1.7; }
.eyebrow { font-size: 12px; letter-spacing: 2px; opacity: .82; }
.feature-badge { display: inline-block; margin-left: 8px; padding: 3px 8px; border-radius: 10px; color: #b42318; background: #fff1f0; box-shadow: 0 3px 10px rgba(0, 0, 0, .12); font-weight: 700; letter-spacing: 1px; opacity: 1; }
.history-link { flex: none; margin-left: 20px; padding: 10px 18px; border: 1px solid rgba(255, 255, 255, .65); border-radius: 20px; color: #fff; }
.workflow-card { border: 0; border-radius: 18px; box-shadow: 0 10px 32px rgba(31, 41, 55, .08); }
.workflow-card /deep/ .el-card__body { padding: 30px; }
.upload-panel, .processing-panel, .download-panel, .terminal-panel { min-height: 390px; display: flex; flex-direction: column; justify-content: center; align-items: center; text-align: center; }
.template-guide { width: 560px; max-width: 100%; display: flex; justify-content: space-between; align-items: center; gap: 18px; margin: 34px 0 18px; padding: 18px 20px; border: 1px solid #dbeafe; border-radius: 12px; text-align: left; background: #f0f7ff; }
.template-guide h2 { margin: 0 0 7px; font-size: 18px; }
.template-guide p { margin: 0; color: #5f6b7a; line-height: 1.6; }
.template-guide .el-button { flex: none; }
.upload-panel /deep/ .el-upload-dragger { width: 560px; max-width: 100%; height: 220px; padding-top: 34px; border-radius: 14px; background: #f8fbff; }
.upload-progress { width: 560px; max-width: 100%; margin-top: 20px; }
.validating-tip { margin: 8px 0 0; color: #1769e0; font-size: 13px; }
.privacy-note { margin-top: 20px; color: #6b7280; }
.privacy-note i { color: #16a085; }
.quote-panel { padding-top: 34px; }
.panel-title { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 24px; }
.panel-title h2 { margin: 0 0 7px; }
.panel-title p { margin: 0; color: #6b7280; }
.main-action, .quote-actions { display: flex; justify-content: center; gap: 12px; margin-top: 26px; }
.processing-icon, .failed-icon, .success-mark { margin-bottom: 18px; font-size: 58px; color: #1769e0; }
.failed-icon { color: #f59e0b; }
.success-mark { color: #16a085; }
.processing-panel h2, .download-panel h2, .terminal-panel h2 { margin: 0 0 10px; }
.processing-panel p, .download-panel p, .terminal-panel p { color: #6b7280; }
.processing-panel /deep/ .el-progress { width: 620px; max-width: 100%; margin: 25px 0 12px; }
.processing-meta { color: #9ca3af; font-size: 13px; }
.processing-cancel { margin-top: 12px; }
.charge-rule { display: flex; justify-content: space-between; align-items: center; margin: 24px 0; padding: 22px 28px; border-radius: 14px; background: linear-gradient(135deg, #f0f7ff, #ecfbf7); }
.charge-rule div { display: flex; flex-direction: column; }
.charge-rule strong { font-size: 34px; color: #e85d04; }
.charge-rule span, .charge-rule p { color: #6b7280; line-height: 1.7; }
.expired-tip { text-align: center; color: #e85d04; }
.pay-dialog { text-align: center; }
.pay-dialog img { display: block; width: 260px; height: 260px; margin: 12px auto; }
.pay-amount { font-size: 30px; font-weight: 700; color: #e85d04; }
.pay-help { color: #6b7280; line-height: 1.6; }
.mobile-sticky { display: none; }

/* 取消任务二次确认弹窗 */
.book-match-page /deep/ .cancel-confirm-dialog {
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(15, 23, 42, .18);
}
.book-match-page /deep/ .cancel-confirm-dialog .el-dialog__header { padding: 0; border-bottom: none; }
.book-match-page /deep/ .cancel-confirm-dialog .el-dialog__headerbtn { display: none; }
.book-match-page /deep/ .cancel-confirm-dialog .el-dialog__body { padding: 28px 28px 8px; }
.book-match-page /deep/ .cancel-confirm-dialog .el-dialog__footer { padding: 12px 24px 24px; border-top: none; }
.cancel-dialog-body { text-align: center; }
.cancel-icon-wrap {
  width: 64px; height: 64px; line-height: 64px;
  margin: 0 auto 14px;
  border-radius: 50%;
  background: linear-gradient(135deg, #fff5e6, #ffe2c2);
  color: #f59e0b;
  font-size: 34px;
  box-shadow: 0 8px 18px rgba(245, 158, 11, .22);
}
.cancel-dialog-body h3 { margin: 0 0 10px; font-size: 18px; color: #1f2937; font-weight: 600; }
.cancel-dialog-body p { margin: 0; color: #6b7280; line-height: 1.7; font-size: 14px; }
.cancel-hint {
  display: inline-flex; align-items: center; gap: 6px;
  margin-top: 16px; padding: 7px 14px;
  border-radius: 999px;
  background: #f0fdf4; color: #16a085;
  font-size: 12px;
}
.cancel-hint i { font-size: 14px; }
.cancel-dialog-footer { display: flex; gap: 12px; justify-content: center; }
.cancel-dialog-footer .el-button {
  flex: 1; height: 42px; border-radius: 999px; font-weight: 500;
  transition: transform .15s ease, box-shadow .15s ease;
}
.cancel-dialog-footer .el-button:hover { transform: translateY(-1px); }
.cancel-dialog-footer .el-button--danger { box-shadow: 0 6px 16px rgba(245, 87, 108, .28); }

@media screen and (max-width: 768px) {
  .book-match-page { padding: 14px 12px 100px; }
  .hero { display: block; padding: 22px 18px; border-radius: 14px; }
  .hero h1 { font-size: 25px; }
  .history-link { display: inline-block; margin: 18px 0 0; }
  .workflow-card /deep/ .el-card__body { padding: 18px 14px; }
  .workflow-card /deep/ .el-steps--vertical { height: 240px; margin-bottom: 12px; }
  .upload-panel { min-height: 320px; }
  .template-guide { display: block; margin-top: 22px; text-align: center; }
  .template-guide .el-button { width: 100%; margin-top: 14px; }
  .upload-panel /deep/ .el-upload, .upload-panel /deep/ .el-upload-dragger { width: 100%; }
  .panel-title { display: block; }
  .panel-title .el-button { padding-left: 0; }
  .main-action { display: none; }
  .charge-rule { display: block; padding: 18px; }
  .charge-rule p { margin-bottom: 0; }
  .quote-actions { flex-direction: column; }
  .quote-actions .el-button { width: 100%; margin-left: 0; }
  .mobile-sticky { display: block; position: fixed; left: 0; right: 0; bottom: 0; z-index: 50; padding: 10px 14px calc(10px + env(safe-area-inset-bottom)); background: #fff; box-shadow: 0 -4px 18px rgba(0, 0, 0, .12); }
  .mobile-sticky .el-button { width: 100%; height: 44px; }
}
</style>
<style lang="less" scoped>
@media screen and (max-width: 768px) {
  .hero { padding-bottom: 12px; margin-bottom: 12px; }
  .workflow-card /deep/ .el-steps--vertical { height: 184px; margin-bottom: 8px; }
  .workflow-card /deep/ .el-step__description { padding-bottom: 6px; }
  .workflow-card /deep/ .el-step__description.is-wait,
  .workflow-card /deep/ .el-step__description.is-success { display: none; }
}
</style>
